// Dependency-free entry point — no React. Consumed by company-switch/fake-access/
// delegation hooks. `PERMISSIONS_QUERY_KEY` is imported from its defining module
// (not the `@/features/access-control` barrel) for the same reason `index.ts`
// in this directory imports `USER_INFO_QUERY_KEY` from a deep path: pulling the
// barrel would drag the React/Query/component graph into a non-React module.
import { queryClient } from '@/legacy/lib/reactQuery';
import { USER_INFO_QUERY_KEY } from '@/features/space-user/api/constants';
import { PERMISSIONS_QUERY_KEY } from '@/features/access-control/api/hooks/usePermissions';
import { switchCredentials, SWITCH_CREDENTIALS_TYPE } from '../api/service/session';
import { loginFakeAccessService } from '../api/service/impersonation';
import { sessionStore } from '../domain/session/sessionStore';
import { makeScopeKey, parseScopeKey } from '../domain/session/scope';
import { DELEGATION_SCOPE } from '../domain/session/types';
import type { AuthScope, ScopeKey, ScopedCredential } from '../domain/session/types';

export const SCOPE_TRANSITION_STATUS = {
	ACTIVE: 'active',
	ACTIVATED: 'activated',
	STALE: 'stale',
	FAILED: 'failed',
} as const;

export type ScopeTransitionStatus =
	(typeof SCOPE_TRANSITION_STATUS)[keyof typeof SCOPE_TRANSITION_STATUS];

export interface ScopeTransitionResult {
	status: ScopeTransitionStatus;
	scopeKey: ScopeKey;
	activeScopeKey: ScopeKey | null;
	error?: unknown;
}

// Monotonic token: the transition that started most recently "wins". A response
// that resolves after a newer transition already started must not activate or
// cancel/remove queries — see the "Rapid switching" scenario in the spec.
let currentToken = 0;

const staleResult = (scopeKey: ScopeKey): ScopeTransitionResult => ({
	status: SCOPE_TRANSITION_STATUS.STALE,
	scopeKey,
	activeScopeKey: sessionStore.getState().activeScopeKey,
});

const cancelAndRemoveQueries = async (): Promise<void> => {
	await queryClient.cancelQueries();
	queryClient.removeQueries();
};

const refetchIdentityAndPermissions = async (): Promise<void> => {
	await queryClient.refetchQueries({ queryKey: [USER_INFO_QUERY_KEY] });
	await queryClient.refetchQueries({ queryKey: PERMISSIONS_QUERY_KEY });
};

// Privileged users stay entirely on `/login/impersonate/:id` — the only endpoint
// that grants a company the caller does not belong to. It already takes the
// delegation selection, so a same-company delegation change re-issues against
// that same company rather than needing a second endpoint, and their flow is
// untouched by the switch-credentials migration. This deliberately revives the
// role branch an earlier refactor retired (see context/auth/README.md): back then
// both endpoints were open to every role, which is no longer true.
//
// Everyone else goes through `/login/switch-credentials`, scoped by the backend to
// the companies and delegations the caller actually owns. Two independent axes:
// `type` names which one moved, while `delegation` is sent only for a SELECTED
// scope — an absent field is how the backend spells "the whole company". That
// keeps the combination working: the modal lets a user pick the target company AND
// one of ITS delegations in a single submit, and that selection must survive the
// company move rather than being reset.
const acquireCredential = async (
	targetScope: AuthScope,
	activeScope: AuthScope | null,
): Promise<ScopedCredential> => {
	const delegationIds =
		targetScope.delegationKind === DELEGATION_SCOPE.SELECTED ? [...targetScope.delegationIds] : [];
	const { isSuperUser, isStaff } = sessionStore.getState();

	if (isSuperUser || isStaff) {
		const { access_token, refresh_token, id_token } = await loginFakeAccessService(
			targetScope.companyId,
			delegationIds,
		);
		return {
			accessToken: access_token,
			refreshToken: refresh_token,
			idToken: id_token ?? null,
		};
	}

	const isSameCompany = activeScope !== null && activeScope.companyId === targetScope.companyId;
	const { access_token, refresh_token, id_token } = await switchCredentials({
		company: targetScope.companyId,
		...(targetScope.delegationKind === DELEGATION_SCOPE.SELECTED
			? { delegation: delegationIds }
			: {}),
		type: isSameCompany ? SWITCH_CREDENTIALS_TYPE.DELEGATION : SWITCH_CREDENTIALS_TYPE.COMPANY,
	});
	return { accessToken: access_token, refreshToken: refresh_token, idToken: id_token };
};

// The target scope equals the currently active one: either it is still usable
// (hit — just touch `lastUsedAt`), or its cache entry is gone while the session's
// flat token fields still hold usable credentials for it (recovery — re-cache
// them without a network call), or neither, which is a failure that changes
// nothing about the current (already active) scope.
const handleSameScope = (targetScope: AuthScope, targetKey: ScopeKey): ScopeTransitionResult => {
	const state = sessionStore.getState();

	if (state.findUsableEntry(targetScope)) {
		state.activateScope(targetScope);
		return {
			status: SCOPE_TRANSITION_STATUS.ACTIVE,
			scopeKey: targetKey,
			activeScopeKey: targetKey,
		};
	}

	if (state.accessToken && state.refreshToken) {
		const intent = state.beginAcquisition(targetScope);
		const committed = state.saveAcquiredCredential(intent, {
			accessToken: state.accessToken,
			refreshToken: state.refreshToken,
			idToken: state.idToken,
		});
		if (committed) {
			return {
				status: SCOPE_TRANSITION_STATUS.ACTIVE,
				scopeKey: targetKey,
				activeScopeKey: targetKey,
			};
		}
	}

	return {
		status: SCOPE_TRANSITION_STATUS.FAILED,
		scopeKey: targetKey,
		activeScopeKey: sessionStore.getState().activeScopeKey,
	};
};

/**
 * Moves the active auth scope to `targetScope`, acquiring fresh credentials for
 * every move to a different scope. Failures and stale (superseded) responses
 * preserve whatever scope was active before this call — they never evict or
 * repopulate a newer scope.
 */
export const transitionAuthScope = async (
	targetScope: AuthScope,
): Promise<ScopeTransitionResult> => {
	const myToken = ++currentToken;
	const isStale = (): boolean => myToken !== currentToken;
	const targetKey = makeScopeKey(targetScope);
	const state = sessionStore.getState();

	if (state.activeScopeKey === targetKey) {
		return handleSameScope(targetScope, targetKey);
	}

	const activeScope = state.activeScopeKey ? parseScopeKey(state.activeScopeKey) : null;

	// No cache lookup for a DIFFERENT scope, deliberately — even though the store
	// now keeps an entry per visited scope (`saveAcquiredCredential`). Moving to
	// another scope always costs one acquisition, because the credential this
	// session holds for the scope it is leaving is the only one the backend keeps
	// alive: anything stored for a previously visited scope was invalidated the
	// moment the current one was issued. Swapping this acquisition for a
	// `findUsableEntry` hit is the one-line change that reintroduces the 401
	// cascade recorded in context/auth/README.md.
	const intent = state.beginAcquisition(targetScope);
	try {
		const credential = await acquireCredential(targetScope, activeScope);
		if (isStale()) return staleResult(targetKey);
		await cancelAndRemoveQueries();
		if (isStale()) return staleResult(targetKey);

		const committed = sessionStore.getState().saveAcquiredCredential(intent, credential);
		if (!committed) {
			return {
				status: SCOPE_TRANSITION_STATUS.FAILED,
				scopeKey: targetKey,
				activeScopeKey: sessionStore.getState().activeScopeKey,
			};
		}

		await refetchIdentityAndPermissions();
		return {
			status: SCOPE_TRANSITION_STATUS.ACTIVATED,
			scopeKey: targetKey,
			activeScopeKey: targetKey,
		};
	} catch (error) {
		return {
			status: SCOPE_TRANSITION_STATUS.FAILED,
			scopeKey: targetKey,
			activeScopeKey: sessionStore.getState().activeScopeKey,
			error,
		};
	}
};
